'use es6';

import { useState } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import unescapedText from 'I18n/utils/unescapedText';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Text } from 'foundations-components/components/Text';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { Button } from 'foundations-components/transitional/components/Button';
import { List } from 'foundations-components/transitional/components/List';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { HR } from 'foundations-components/transitional/components/HR';
import { DeleteIcon } from 'foundations-assets';
import PagesLinkSelect from 'simple-menus-lib/components/pages/PagesLinkSelect';
import withConnectedMenuOptions from 'simple-menus-lib/components/menu/decorators/withConnectedMenuOptions';
import withDeleteMenuNodeModal from 'simple-menus-lib/components/menu/decorators/withDeleteMenuNodeModal';
import { MenuItemActions, MenuNodeTypes } from '../../constants/menus';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PrimaryActions = [MenuItemActions.MENU_TREE_ADD_CHILD, MenuItemActions.MENU_TREE_ADD_NODE, MenuItemActions.MENU_TREE_CLONE_NODE];
const SwapActions = [MenuNodeTypes.NO_LINK, MenuNodeTypes.PAGE_LINK, MenuNodeTypes.PAGE_LINK_WITH_PARAMS, MenuNodeTypes.URL_LINK];
function MenuItemLinkInputs({
  type,
  linkUrl,
  linkParams,
  pageLinkId,
  pageLinkName,
  pageEditorUrl,
  isPublished,
  isVisible,
  readOnly,
  onLinkUrlChange,
  onPageLinkIdChange,
  onLinkParamsChange
}) {
  const selectedPageName = pageLinkName || unescapedText('simpleMenusLib.components.menuItemOptions.options.PAGE_LINK.placeholder');
  const handlePageLinkIdChange = evt => {
    onPageLinkIdChange(evt.target.value);
  };
  const PagesSelectDropdown = /*#__PURE__*/_jsx(PagesLinkSelect, {
    isPublished: isPublished,
    isVisible: isVisible,
    readOnly: readOnly,
    selectedPageId: pageLinkId,
    selectedPageName: selectedPageName,
    selectedPageEditorUrl: pageEditorUrl,
    onChange: handlePageLinkIdChange
  });
  return /*#__PURE__*/_jsxs(Box, {
    flex: "1 0 auto",
    maxInlineSize: "calc(100% - 124px)",
    children: [type === MenuNodeTypes.NO_LINK && /*#__PURE__*/_jsx(TextInput, {
      "data-test-id": MenuNodeTypes.NO_LINK,
      disabled: true,
      styled: false,
      placeholder: unescapedText('simpleMenusLib.components.menuItemOptions.options.NO_LINK.placeholder')
    }), type === MenuNodeTypes.URL_LINK && /*#__PURE__*/_jsx("div", {
      draggable: true,
      onDragStart: e => {
        // When trying to highlight text prevent the entire component from dragging
        e.preventDefault();
        e.stopPropagation();
      },
      children: /*#__PURE__*/_jsx(TextInput, {
        "data-test-id": "url-link-input",
        placeholder: unescapedText('simpleMenusLib.components.menuItemOptions.options.URL_LINK.placeholder'),
        defaultValue: linkUrl,
        onChange: onLinkUrlChange,
        disabled: readOnly
      })
    }), type === MenuNodeTypes.PAGE_LINK && PagesSelectDropdown, type === MenuNodeTypes.PAGE_LINK_WITH_PARAMS && /*#__PURE__*/_jsx("div", {
      draggable: true,
      onDragStart: e => {
        // When trying to highlight text prevent the entire component from dragging
        e.preventDefault();
        e.stopPropagation();
      },
      children: /*#__PURE__*/_jsxs(Grid, {
        alignItems: "center",
        inlineSize: "100%",
        margin: "0",
        children: [/*#__PURE__*/_jsx(GridItem, {
          padding: "0",
          marginInlineStart: "-200",
          size: 6,
          children: PagesSelectDropdown
        }), /*#__PURE__*/_jsx(GridItem, {
          padding: "0",
          marginInlineStart: "200",
          size: 6,
          children: /*#__PURE__*/_jsx(TextInput, {
            "data-test-id": "page-link-with-params-input",
            placeholder: unescapedText('simpleMenusLib.components.menuItemOptions.options.PAGE_LINK_WITH_PARAMS.placeholder'),
            defaultValue: linkParams,
            onChange: onLinkParamsChange,
            readOnly: readOnly
          })
        })]
      })
    })]
  });
}
function MenuItemActionsDropdown({
  type,
  isExternal,
  editorUrl,
  onOpenChange,
  onExternalCheckboxChange,
  onAddChildClick,
  onAddNodeBelowClick,
  onCloneNodeClick,
  onReplaceNodeClick
}) {
  const primaryActionHandlers = {
    [MenuItemActions.MENU_TREE_ADD_CHILD]: onAddChildClick,
    [MenuItemActions.MENU_TREE_ADD_NODE]: onAddNodeBelowClick,
    [MenuItemActions.MENU_TREE_CLONE_NODE]: onCloneNodeClick
  };
  const PrimaryActionButtons = PrimaryActions.map(action => /*#__PURE__*/_jsx(Button, {
    onClick: primaryActionHandlers[action],
    children: unescapedText(`simpleMenusLib.components.menuItemOptions.actions.primaryOptions.${action}`)
  }, action));
  const SecondaryActionButtons = SwapActions.filter(action => action !== type).map(action => /*#__PURE__*/_jsx(Button, {
    "data-test-id": action,
    paddingInlineStart: "1000",
    onClick: () => onReplaceNodeClick(action),
    children: unescapedText(`simpleMenusLib.components.menuItemOptions.actions.swapOptions.${action}`)
  }, action));
  function handleExternalCheckboxChange({
    target: {
      checked
    }
  }) {
    onExternalCheckboxChange(checked);
  }
  return /*#__PURE__*/_jsx(Dropdown, {
    "data-test-id": "menu-item-action-button",
    buttonUse: "link",
    buttonText: unescapedText('simpleMenusLib.components.menuItemOptions.actions.label'),
    onOpenChange: onOpenChange,
    children: /*#__PURE__*/_jsxs(List, {
      wrapChildren: true,
      children: [PrimaryActionButtons, editorUrl && /*#__PURE__*/_jsx(Button, {
        href: editorUrl,
        external: true,
        children: unescapedText('simpleMenusLib.components.menuItemOptions.actions.openInEditor.label')
      }), /*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx(Text, {
        paddingInline: "500",
        paddingBlock: "200",
        margin: "0",
        textTransform: "uppercase",
        children: unescapedText(`simpleMenusLib.components.menuItemOptions.actions.primaryOptions.${MenuItemActions.MENU_NODE_REPLACE}`)
      }), /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        children: SecondaryActionButtons
      }), /*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx(Checkbox, {
        checked: isExternal,
        onChange: handleExternalCheckboxChange,
        children: unescapedText('simpleMenusLib.components.menuItemOptions.actions.external')
      })]
    })
  });
}
function MenuItemOptions({
  type,
  readOnly,
  editorUrl,
  addChild,
  addNodeBelow,
  cloneNode,
  replaceNode,
  isExternal,
  setLinkExternal,
  linkUrl,
  linkParams,
  isPublished,
  isVisible,
  pageLinkId,
  pageLinkName,
  onLinkUrlChange,
  onLinkParamsChange,
  onPageLinkIdChange,
  deleteNode,
  deleteMenuNodeModal
}) {
  const [isHovering, setIsHovering] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const showMenuItemActions = isHovering || isOpen;
  function onMouseEnter() {
    if (readOnly) return;
    setIsHovering(true);
  }
  function onMouseLeave() {
    setIsHovering(false);
  }
  function onOpenChange({
    target: {
      value
    }
  }) {
    // Allow dropdown to trigger onChange before switching
    setTimeout(() => setIsOpen(value), 0);
  }
  return /*#__PURE__*/_jsxs(Flex, {
    "data-test-id": "simple-menu-action-options",
    alignItems: "center",
    blockSize: "100%",
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    children: [/*#__PURE__*/_jsx(MenuItemLinkInputs, {
      type: type,
      linkUrl: linkUrl,
      linkParams: linkParams,
      pageLinkId: pageLinkId,
      pageLinkName: pageLinkName,
      pageEditorUrl: editorUrl,
      isPublished: isPublished,
      isVisible: isVisible,
      readOnly: readOnly,
      onLinkUrlChange: onLinkUrlChange,
      onPageLinkIdChange: onPageLinkIdChange,
      onLinkParamsChange: onLinkParamsChange
    }), showMenuItemActions && /*#__PURE__*/_jsxs(Box, {
      marginInlineStart: "400",
      display: "flex",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(MenuItemActionsDropdown, {
        type: type,
        isHovering: isHovering,
        isOpen: isOpen,
        isExternal: isExternal,
        editorUrl: editorUrl,
        onOpenChange: onOpenChange,
        onExternalCheckboxChange: setLinkExternal,
        onAddChildClick: addChild,
        onAddNodeBelowClick: addNodeBelow,
        onCloneNodeClick: cloneNode,
        onReplaceNodeClick: replaceNode
      }), /*#__PURE__*/_jsx(IconButton, {
        marginInlineStart: "200",
        use: "link",
        onClick: deleteNode,
        children: /*#__PURE__*/_jsx(DeleteIcon, {})
      })]
    }), deleteMenuNodeModal]
  });
}
MenuItemOptions.propTypes = {
  addChild: PropTypes.func.isRequired,
  addNodeBelow: PropTypes.func.isRequired,
  cloneNode: PropTypes.func.isRequired,
  deleteMenuNodeModal: PropTypes.node,
  deleteNode: PropTypes.func.isRequired,
  editorUrl: PropTypes.string,
  isPublished: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  linkUrl: PropTypes.string,
  linkParams: PropTypes.string,
  isExternal: PropTypes.bool,
  onLinkParamsChange: PropTypes.func.isRequired,
  onLinkUrlChange: PropTypes.func.isRequired,
  onPageLinkIdChange: PropTypes.func.isRequired,
  pageLinkId: PropTypes.number,
  pageLinkName: PropTypes.string,
  readOnly: PropTypes.bool,
  replaceNode: PropTypes.func.isRequired,
  setLinkExternal: PropTypes.func.isRequired,
  type: PropTypes.oneOf(Object.keys(MenuNodeTypes))
};
export default compose(withConnectedMenuOptions(), withDeleteMenuNodeModal())(MenuItemOptions);